import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpCircle, Car, Heart, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { CarCard, CarCardSkeleton } from '@/components/CarCard';
import { Badge, Button, EmptyState } from '@/components/ui';
import { BrandMark } from '@/components/BrandMark';
import { CarSilhouette } from '@/components/CarSilhouette';
import { useMakeLookup } from '@/hooks/useApp';
import { formatPrice } from '@/lib/format';
import type { ListingSummary, PagedResult } from '@/lib/types';

export function MyListingsPage() {
  const queryClient = useQueryClient();
  const makes = useMakeLookup();

  const { data, isPending } = useQuery({
    queryKey: ['my-listings'],
    queryFn: () => api<PagedResult<ListingSummary>>('/api/listings/mine?page=1&pageSize=50'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['my-listings'] });

  const bump = useMutation({
    mutationFn: (id: string) => api(`/api/listings/${id}/bump`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const markSold = useMutation({
    mutationFn: (id: string) => api(`/api/listings/${id}/sold`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/listings/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-body">Moji oglasi</h1>
        <Link to="/postavi-oglas" className="hidden sm:block">
          <Button>Postavi oglas</Button>
        </Link>
      </div>

      {isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="skeleton h-28 rounded-[var(--radius-panel)]" />
          ))}
        </div>
      ) : data && data.items.length > 0 ? (
        <ul className="space-y-3">
          {data.items.map((listing, index) => (
            <li
              key={listing.id}
              style={{ '--i': Math.min(index, 11) } as React.CSSProperties}
              className="animate-fade-in-up stagger flex flex-col gap-3 rounded-[var(--radius-panel)] border border-default bg-raised p-3 shadow-card transition duration-300 hover:border-strong sm:flex-row sm:items-center sm:gap-4 sm:p-4"
            >
              {/*
                Photo and title stay side by side at every width. Stacking them the way the row
                stacks would put a small square on a line of its own with the rest of the card
                empty beside it, which is a lot of scrolling to see four of your own adverts.
              */}
              <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                <Link
                  to={`/oglas/${listing.shortCode}`}
                  viewTransition
                  className="size-20 shrink-0 overflow-hidden rounded-xl bg-sunken sm:size-24"
                >
                  {listing.thumbnailUrl ? (
                    <img
                      src={listing.thumbnailUrl}
                      alt=""
                      loading="lazy"
                      className="size-full object-cover transition duration-500 hover:scale-105"
                    />
                  ) : (
                    <span className="flex size-full items-center justify-center text-faint">
                      <CarSilhouette bodyType={listing.bodyType} className="w-12 opacity-50" />
                    </span>
                  )}
                </Link>

                <div className="min-w-0 flex-1">
                  <Link
                    to={`/oglas/${listing.shortCode}`}
                    viewTransition
                    className="flex items-center gap-2 font-semibold text-body transition hover:text-accent"
                  >
                    <BrandMark
                      make={makes.get(listing.makeName) ?? { name: listing.makeName }}
                      size="xs"
                    />
                    <span className="truncate">
                      {listing.makeName} {listing.modelName}
                    </span>
                  </Link>
                  <p className="mt-1 text-sm text-muted tabular-nums">
                    {listing.year}. · {formatPrice(listing.price)}
                  </p>
                  <p className="mt-1 text-xs text-faint">Šifra: {listing.shortCode}</p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <Link to={`/uredi-oglas/${listing.shortCode}`} viewTransition className="flex-1 sm:flex-none">
                  <Button variant="secondary" size="sm" className="w-full">
                    <Pencil size={15} />
                    Izmeni
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => bump.mutate(listing.id)}
                  disabled={bump.isPending}
                  title="Podigni oglas na vrh"
                >
                  <ArrowUpCircle size={15} />
                  Osveži
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => markSold.mutate(listing.id)}
                  disabled={markSold.isPending}
                >
                  Prodato
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    // Deleting an ad throws away its history and any conversations attached to it,
                    // so it asks first. Bumping and marking sold are reversible and do not.
                    if (window.confirm('Da li sigurno želite da obrišete ovaj oglas?')) {
                      remove.mutate(listing.id);
                    }
                  }}
                  disabled={remove.isPending}
                  aria-label="Obriši oglas"
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<Car size={40} />}
          title="Još nemate oglasa"
          description="Postavite prvi oglas — besplatno je i traje 60 dana."
          action={
            <Link to="/postavi-oglas">
              <Button>Postavi oglas</Button>
            </Link>
          }
        />
      )}
    </div>
  );
}

export function FavouritesPage() {
  const { data, isPending } = useQuery({
    queryKey: ['favourites'],
    queryFn: () => api<PagedResult<ListingSummary>>('/api/listings/favourites?page=1&pageSize=50'),
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="mb-6 flex items-center gap-2 text-2xl font-bold text-body">
        Sačuvani oglasi
        {data && data.total > 0 && <Badge tone="accent">{data.total}</Badge>}
      </h1>

      {isPending ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <CarCardSkeleton key={index} index={index} />
          ))}
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {data.items.map((listing, index) => (
            <CarCard key={listing.id} listing={listing} index={index} />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Heart size={40} />}
          title="Nema sačuvanih oglasa"
          description="Kliknite na srce na oglasu da ga sačuvate za kasnije."
          action={
            <Link to="/">
              <Button>Pretraži oglase</Button>
            </Link>
          }
        />
      )}
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="mx-auto max-w-xl px-4 py-24">
      <EmptyState
        title="Stranica ne postoji"
        description="Link je možda pogrešan ili je stranica uklonjena."
        action={
          <Link to="/">
            <Button>Nazad na početnu</Button>
          </Link>
        }
      />
    </div>
  );
}
